/** @jsx jsx */
import { jsx } from 'theme-ui'
import { Box, Flex, Button, IconButton } from '@theme-ui/components'
import PropTypes from 'prop-types'
import { Carousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import {
  Children,
  cloneElement,
  Fragment,
  useEffect,
  useRef,
  useState
} from 'react'
import ArrowBack from '~/assets/images/icons/s73-arrow-left.svg'
import ArrowNext from '~/assets/images/icons/s73-arrow-right.svg'
import ImgLoader from '../ImgLoader'
import Close from '~/assets/images/icons/plus-icon.svg'
import 'photoswipe/dist/photoswipe.css'
import 'photoswipe/dist/default-skin/default-skin.css'
import { Gallery, Item } from 'react-photoswipe-gallery'

const ImageCarousel = ({
  primaryImage,
  secondaryImages,
  children,
  sliderHeight = null,
  forwardSx = {},
  objectFit = 'contain',
  enableZoom = false
}) => {
  const itemsLength = Children.count(children) + secondaryImages.length + 1
  const [zoomedImg, setZoomedImg] = useState(null)
  const [extraZoom, setExtraZoom] = useState(false)
  const [orig, setOrig] = useState({ x: null, y: null })
  const ref = useRef()

  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (!zoomedImg) {
      setExtraZoom(false)
      document.documentElement.style.overflow = 'auto'
      document.body.querySelector('main').style.overflow = 'auto'
    } else {
      document.documentElement.style.overflow = 'hidden'
      document.body.querySelector('main').style.overflow = 'hidden'
    }
  }, [zoomedImg])

  const exitZoom = () => {
    setZoomedImg(null)
    setOrig({ x: null, y: null })
  }

  // Having a null among children will cause an additional slide on the Carousel
  // component so we need to clear that out before-hand
  const slides = [primaryImage, ...secondaryImages].filter(Boolean)

  function createThumbNav(gallery) {
    const items = gallery.itemHolders
    const allItems = gallery.items
    const currentIndex = gallery.getCurrentIndex()
    const prev =
      currentIndex === 0
        ? allItems[allItems.length - 1]
        : allItems[currentIndex - 1]
    const next =
      currentIndex === allItems.length - 1
        ? allItems[0]
        : allItems[currentIndex + 1]
    return { prev: prev.src, next: next.src }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const ctrlDotsLi = document.querySelectorAll('.control-dots li')
      ctrlDotsLi.forEach(item => {
        item.setAttribute('role', 'listitem')
      })
    }
  }, [])

  return (
    <Fragment>
      <Box
        sx={{
          '--slider-height': sliderHeight || [
            '25rem',
            '40rem',
            '62rem',
            null,
            '74.8rem'
          ],
          marginBottom: '4rem',
          '.slide': {
            background: 'none',
            position: 'relative'
          },
          '.carousel-slider': {
            height: 'var(--slider-height)',
            overflow: 'visible'
          },
          '.carousel-slider .slider-wrapper': { height: '100%' },
          '.carousel-slider .slider-wrapper .slider': { height: '100%' },
          '.carousel-slider .slider-wrapper .slider .slide': { height: '100%' },
          '.carousel .control-dots': {
            top: 'calc(100% + 2rem)',
            bottom: 'initial',
            left: '50%',
            width: 'min(100%, 62rem)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transform: 'translateX(-50%)',
            height: '15px',
            margin: 0,
            padding: '0 10px'
          },
          '.carousel .dot': {
            height: ['3px', '2.25px', '1.87', '3.5px'],
            width: '100%',
            maxWidth: '20px',
            backgroundColor: '#5D5D5D',
            borderRadius: '2px',
            boxShadow: 'none',
            margin: 0,
            opacity: 1,
            mr: ['10px', '20px', '20px']
          },
          '.carousel .dot.selected': {
            height: ['5.68px', '4.71px', '8.8px'],
            backgroundColor: 'rgba(0,0,0,0)',
            border: '1px solid black',
            transition: 'height 0.25s ease'
          },
          'button svg': {
            width: ['1rem', '1.2rem', '2.2rem'],
            height: ['1rem', '1.2rem', '2.2rem'],
            color: 'black'
          },
          '.prev-arrow': {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            px: ['2.5rem', '1.5rem', '3rem'],
            zIndex: '5'
          },
          '.next-arrow': {
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            px: ['2.5rem', '1.5rem', '3rem'],
            zIndex: '5'
          },
          '.pswp__img': {
            objectFit: 'contain'
          },
          '.pswp__bg': {
            backgroundColor: 'white'
          },
          '.pswp__js_prev, .pswp__js_next': {
            width: '120px',
            pointerEvents: 'none',
            display: ['none', null, null, 'block']
          },
          '.pswp__js_prev': {
            ml: '53px'
          },
          '.pswp__js_next': {
            mr: '53px'
          },
          '.pswp__button--arrow--left, .pswp__button--arrow--right': {
            width: 'auto !important',
            opacity: 1
          },
          ...forwardSx
        }}
      >
        <Gallery
          shareButton={false}
          onOpen={photoswipe => {
            const yotpo = document.querySelector('.yotpo.testimonials')
            if (yotpo) {
              yotpo.style.display = 'none'
            }

            const nextBtn = document.querySelector(
              '.pswp__button--arrow--right'
            )
            if (nextBtn && !nextBtn.querySelector('img')) {
              const nextThumb = document.createElement('img')
              nextThumb.src = createThumbNav(photoswipe).next
              nextThumb.className = 'pswp__js_next'
              nextBtn.appendChild(nextThumb)
            }
            const prevBtn = document.querySelector('.pswp__button--arrow--left')
            if (prevBtn && !prevBtn.querySelector('img')) {
              const prevThumb = document.createElement('img')
              prevThumb.src = createThumbNav(photoswipe).prev
              prevThumb.className = 'pswp__js_prev'
              prevBtn.appendChild(prevThumb)
            }
            photoswipe.listen('close', () => {
              const yotpo = document.querySelector('.yotpo.testimonials')
              if (yotpo) {
                yotpo.style.display = 'block'
              }
            })
            photoswipe.listen('afterChange', () => {
              const prevThumb = document.querySelector('.pswp__js_prev')
              const nextThumb = document.querySelector('.pswp__js_next')
              if (prevThumb) prevThumb.src = createThumbNav(photoswipe).prev
              if (nextThumb) nextThumb.src = createThumbNav(photoswipe).next
            })
          }}
          options={{
            arrowPrevSVG: '',
            arrowNextSVG: '',
            showHideOpacity: true,
            getThumbBoundsFn: false,
            showAnimationDuration: 200,
            getDoubleTapZoom: function (isMouseClick, item) {
              if (isMouseClick) {
                return 4
              } else {
                return 1
              }
            },
            maxSpreadZoom: 4
          }}
        >
          <Carousel
            onChange={index => setCurrentIndex(index)}
            showIndicators={true}
            showStatus={false}
            showArrows={true}
            showThumbs={false}
            autoPlay={false}
            infiniteLoop={true}
            renderArrowPrev={(onClick, label) =>
              itemsLength > 1 ? (
                <Button
                  className="prev-arrow"
                  variant="iconOnly"
                  onClick={onClick}
                  aria-label={label}
                >
                  <ArrowBack />
                </Button>
              ) : null
            }
            renderArrowNext={(onClick, label) =>
              itemsLength > 1 ? (
                <Button
                  className="next-arrow"
                  variant="iconOnly"
                  onClick={onClick}
                  aria-label={label}
                >
                  <ArrowNext />
                </Button>
              ) : null
            }
          >
            {slides.map((image, i) => {
              return (
                <Item
                  original={image.gatsbyImageData?.images?.fallback?.src}
                  thumbnail={image.gatsbyImageData?.images?.fallback?.src}
                  width="1500"
                  height="1000"
                >
                  {({ ref, open }) => (
                    <div
                      ref={ref}
                      style={{ height: '100%', cursor: 'pointer' }}
                      onClick={open}
                    >
                      <ImgLoader
                        alt={image.description ?? image.title}
                        image={image}
                        id={'pswipe-' + i}
                        forwardSx={{
                          pointerEvents: 'auto !important',
                          height: '100%'
                        }}
                        imgStyle={{
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  )}
                </Item>
              )
            })}
          </Carousel>
        </Gallery>
      </Box>
    </Fragment>
  )
}

ImageCarousel.propTypes = {
  primaryImage: PropTypes.object.isRequired,
  secondaryImages: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node
}

ImageCarousel.defaultProps = {
  secondaryImages: [],
  children: null
}

export default ImageCarousel
